import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, ListChecks, ShoppingCart, Package, Sparkles, Users, Building2, LogOut, ExternalLink } from "lucide-react";
import { getToken, adminLogout, adminMe } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/malls", label: "Malls", icon: Building2 },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/leads", label: "Leads", icon: ListChecks },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/inventory", label: "Inventory", icon: Package },
  { to: "/admin/plans", label: "AI Plans", icon: Sparkles },
  { to: "/admin/clients", label: "Clients", icon: Users },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then(setUser)
      .catch(() => {
        adminLogout();
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  function onLogout() {
    adminLogout();
    nav("/admin/login", { replace: true });
  }

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500">Checking session…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/10 bg-[#080808] hidden lg:flex lg:flex-col">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/admin" className="font-display text-2xl tracking-tighter">
            Fund<span className="text-amber-500">le</span>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-500 mt-1">Console</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-amber-500/10 text-amber-500 border-l-2 border-amber-500" : "text-neutral-300 hover:bg-white/5"
                }`
              }
            >
              <item.icon size={16} strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 text-xs text-neutral-500 hover:text-amber-500">
            <ExternalLink size={12} /> View public site
          </Link>
          <button onClick={onLogout} data-testid="admin-logout" className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-xs text-neutral-300 hover:text-amber-500">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 glass border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="font-display text-lg">Fund<span className="text-amber-500">le</span></Link>
        <button onClick={onLogout} className="text-xs text-amber-500"><LogOut size={14} className="inline -mt-0.5" /> Sign out</button>
      </div>

      {/* Mobile nav scroller */}
      <div className="lg:hidden fixed top-12 left-0 right-0 z-20 bg-[#080808] border-b border-white/10 overflow-x-auto no-scrollbar">
        <div className="flex gap-1 px-3 py-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap text-xs px-3 py-1.5 ${isActive ? "text-amber-500 border-b border-amber-500" : "text-neutral-400"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 lg:pt-0 pt-28">
        <div className="max-w-[1400px] mx-auto p-6 lg:p-10">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  );
}
